
import { defineComponent, reactive, ref, watch, computed, ComputedRef, Ref } from 'vue'
import { ElForm } from 'element-plus'
import UserLoginWrapper from '@/components/auth-wrappers/UserLoginWrapper.vue'
import InputGroupDark from '@/components/layout/InputGroupDark.vue'
import DoubleScroll from '@/components/layout/DoubleScroll.vue'
import ProximityInfo from '@/components/layout/ProximityInfo.vue'
import { useRoute, useRouter } from 'vue-router'
import Logger from '@/services/logger'
import { RequestHeaderProximityForm } from '@/types/forms'
import { ProximityJudgeDiscussion } from '@/api'
import { proximityRule } from '@/constants/rules'
import JudgeDiscussionSend from '@/components/actions/JudgeDiscussionSend.vue'
import { P_JD_DONE, P_JD_NONE } from '@/constants/statuses'
import InputLabel from '@/components/inputs/InputLabel.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'
import JrRadioGroup from '@/components/jr-parts/JrRadioGroup.vue'
import JrApplicantTitle from '@/components/jr-parts/JrApplicantTitle.vue'
import JrCheckbox from '@/components/jr-parts/JrCheckbox.vue'

export default defineComponent({
  components: {
    JudgeDiscussionSend,
    InputGroupDark,
    UserLoginWrapper,
    ProximityInfo,
    DoubleScroll,
    InputLabel,
    JrTextarea,
    JrRadioGroup,
    JrApplicantTitle,
    JrCheckbox,
  },
  setup() {
    const form = reactive<ProximityJudgeDiscussion>({})
    const formRef = ref<InstanceType<typeof ElForm>>()
    const route = useRoute()
    const router = useRouter()
    const detail = ref<RequestHeaderProximityForm | null>(null)
    const requestHeaderId = computed((): number => {
      return Number(route.params.request_header_id)
    })
    const isJDCheckBoxDisabled = computed((): boolean => {
      return !(form.judge_discussion_status === P_JD_DONE)
    })

    watch(
      () => detail.value,
      () => {
        service.asyncForm(form, detail)
      }
    )

    // ロジッククラス
    const service = (function () {
      const module = {
        ENUM: {
          PROXIMITY_JUDGE_DISCUSSION_STATUS: {
            P_JD_DONE,
            P_JD_NONE,
            P_JD_UNSELECTED: -1,
          },
          PROXIMITY_JUDGE_DISCUSSION_REMARK_INIT: '', //要否判断備考初期値
        },
        methods: function () {
          const ENUM = this.ENUM
          return {
            /**
             * 読み込み時処理
             * @param requestHeaderId ComputedRef<number>
             * @return void
             */
            load: (requestHeaderId: ComputedRef<number>): void => {
              if (Number.isNaN(requestHeaderId.value)) {
                Logger.info('申請基本IDが正しくない')
                router.push({ name: 'NotFound' })
              }
            },
            /**
             * フォーム同期
             * @param proximity ProximityJudgeDiscussion
             * @param requestHeaderDetail RequestHeaderProximityForm
             */
            asyncForm: function () {
              const status = ENUM.PROXIMITY_JUDGE_DISCUSSION_STATUS
              return {
                main: function (
                  proximity: ProximityJudgeDiscussion,
                  requestHeaderDetail: Ref<RequestHeaderProximityForm | null>
                ) {
                  if (!requestHeaderDetail.value?.proximity) {
                    return
                  }
                  const originProximity = requestHeaderDetail.value.proximity
                  // 要否判断ステータス
                  this.asyncJudgeDiscussionStatus(proximity, originProximity.judge_discussion_status)
                  // 要否判断事前協議不要フラグ
                  proximity.is_judge_discussion_prior_discussion_unneeded =
                    originProximity.is_judge_discussion_prior_discussion_unneeded
                      ? originProximity.is_judge_discussion_prior_discussion_unneeded
                      : false
                  // 要否判断現地立会不要フラグ
                  proximity.is_judge_discussion_witness_unneeded = originProximity.is_judge_discussion_witness_unneeded
                    ? originProximity.is_judge_discussion_witness_unneeded
                    : false
                  // 要否判断備考
                  proximity.judge_discussion_remark = originProximity.judge_discussion_remark
                    ? originProximity.judge_discussion_remark
                    : ENUM.PROXIMITY_JUDGE_DISCUSSION_REMARK_INIT
                },
                // 要否判断ステータス同期
                asyncJudgeDiscussionStatus: (
                  proximity: ProximityJudgeDiscussion,
                  judgeDiscussionStatus: number | undefined
                ): void => {
                  // 要否判断が要の場合、ラジオボタンの初期値を「必要」とする
                  if (judgeDiscussionStatus === status.P_JD_DONE) {
                    proximity.judge_discussion_status = status.P_JD_DONE
                    return
                  }
                  // 要否判断が不要の場合、ラジオボタンの初期値を「不要」とする
                  if (judgeDiscussionStatus === status.P_JD_NONE) {
                    proximity.judge_discussion_status = status.P_JD_NONE
                    return
                  }
                  // 要否判断が判断前または伺い中の場合、ラジオボタンの初期値を未選択とする
                  proximity.judge_discussion_status = status.P_JD_UNSELECTED
                },
              }
            },
          }
        },
      }
      return {
        // アクセス可能なメソッド
        load: (requestHeaderId: ComputedRef<number>): void => {
          module.methods().load(requestHeaderId)
        },
        asyncForm: (form: ProximityJudgeDiscussion, detail: Ref<RequestHeaderProximityForm | null>): void => {
          module.methods().asyncForm().main(form, detail)
        },
      }
    })()

    // 初期処理
    service.load(requestHeaderId)
    return {
      form,
      formRef,
      detail,
      requestHeaderId,
      proximityRule,
      P_JD_DONE,
      P_JD_NONE,
      isJDCheckBoxDisabled,
    }
  },
})
